/* =============================================================
   RONCASA — INDIVIDUAL SCHOOL PAGE (page.school.json)
   Renders every scp-* section from its own JSON metafield payload, embedded
   server-side as a <script type="application/json"> block per section (same
   pattern as schools-full-listing.liquid's #sch-area-data) — Liquid stays
   thin, all the row/table building happens here.

   RING MEMBERSHIP vs DISPLAYED DISTANCE (updated 8 Aug 2026, carried from
   build_school_nearby.py — must not be dropped on the way to the page):
   which projects/bus stops COUNT as "within 1km" is now decided by
   ST_CONTAINS against MOE's own school_building_proximity building-boundary
   polygons, not a straight-line radius — so the stat strip / project count
   / nearby list ARE MOE-accurate ring membership, and copy may say so
   plainly. The individual distance_m figure shown per project/stop is
   still straight-line, project point to the school's point coordinate —
   display-only, not the inclusion test, and can legitimately read >1000m
   even for a project the ring correctly includes (a large campus's polygon
   can reach further than its centroid). Never imply the per-row metres
   figure itself is the boundary measurement.
   ============================================================= */
(function () {
  'use strict';

  function readJSON(id) {
    var el = document.getElementById(id);
    if (!el) return null;
    try { return JSON.parse(el.textContent); } catch (e) { console.warn('[roncasa] ' + id + ' JSON parse failed', e); return null; }
  }

  var ONEMAP_SCHOOL_URL = 'https://www.onemap.gov.sg/school';
  // Shared "official check" line: OneMap School Query shows only bands
  // (within 1 km / 1-2 km), never metres, and has no deep-link params.
  function onemapCheckHtml() {
    return '<div class="scp-ring-official">Official check: <a href="' + ONEMAP_SCHOOL_URL +
      '" target="_blank" rel="noopener">Check on OneMap School Query &rarr;</a> ' +
      '(enter the postal code). MOE measures from the school boundary to the building, so it can differ slightly from a straight-line figure.</div>';
  }

  function fmtPsf(n) { return (n === null || n === undefined) ? '—' : '$' + Math.round(n).toLocaleString(); }
  function fmtDist(m) {
    if (m === null || m === undefined) return '—';
    return m < 1000 ? Math.round(m) + 'm' : (m / 1000).toFixed(2) + 'km';
  }
  function fmtMonth(iso) {
    if (!iso) return '—';
    var d = new Date(iso + 'T00:00:00');
    if (isNaN(d.getTime())) return iso;
    return d.toLocaleDateString('en-SG', { month: 'short', year: 'numeric' });
  }

  // ── Stat strip (sch.stats_json — see compute_stat_tiles() in
  // build_school_nearby.py) — a pre-filtered array of {key,label,value,note?}
  // tiles; a tile is simply absent when its underlying value is missing, so
  // nothing here needs its own blank-check per field. `note` is a short
  // population-relative tag (e.g. "Above typical ring depth"), never a
  // sentence — same markup as the cost strip's scp-cost-tile below.
  (function renderStats() {
    var root = document.getElementById('scp-stats-root');
    var data = readJSON('scp-stats-data');
    if (!root || !data || !data.length) return;
    root.innerHTML = '<div class="scp-stats-row">' + data.map(function (t) {
      var note = t.note ? '<div class="scp-stat-note">' + t.note + '</div>' : '';
      return '<div class="scp-cost-tile"><div class="scp-cost-k">' + t.label + '</div><div class="scp-cost-v">' + t.value + '</div>' + note + '</div>';
    }).join('') + '</div>';
  })();

  // ── Profile ─────────────────────────────────────────────────────────
  (function renderProfile() {
    var root = document.getElementById('scp-profile-root');
    var data = readJSON('scp-profile-data');
    if (!root || !data) return;

    function chipList(items) {
      if (!items || !items.length) return '<div class="scp-empty-note">Not listed on MOE Schoolfinder.</div>';
      return '<div class="scp-tag-list">' + items.map(function (t) { return '<span class="scp-tag">' + t + '</span>'; }).join('') + '</div>';
    }

    var html = '<div class="scp-profile-grid">';
    html += '<div class="scp-profile-block"><div class="k">Subjects offered</div>' + chipList(data.subjects) + '</div>';
    html += '<div class="scp-profile-block"><div class="k">Electives &amp; programmes</div>' + chipList(data.electives) + '</div>';
    html += '<div class="scp-profile-block"><div class="k">Co-curricular activities</div>' + chipList(data.ccas) + '</div>';
    html += '<div class="scp-profile-block"><div class="k">Support for special educational needs</div>' + chipList(data.sen_support) + '</div>';
    html += '<div class="scp-profile-block"><div class="k">Contact</div>' +
      (data.website ? '<div class="scp-contact-row"><span>Website</span><a href="' + data.website + '" target="_blank" rel="noopener noreferrer">Visit site &rarr;</a></div>' : '') +
      (data.phone ? '<div class="scp-contact-row"><span>Phone</span><b>' + data.phone + '</b></div>' : '') +
      (data.email ? '<div class="scp-contact-row"><span>Email</span><a href="mailto:' + data.email + '">' + data.email + '</a></div>' : '') +
      '</div>';
    html += '</div>';
    root.innerHTML = html;
  })();

  // ── History / take facts strip (founding year + motto, if present) ────
  // The #scp-history-root container (and the whole "School History" section
  // around it) only exists in the DOM when school-profile.liquid found at
  // least one fact server-side — no empty-state branch needed here anymore.
  (function renderHistory() {
    var root = document.getElementById('scp-history-root');
    var data = readJSON('scp-history-data');
    if (!root || !data) return;
    var html = '<div class="scp-profile-grid">';
    // Hide each fact entirely when absent, rather than rendering a "—"
    // placeholder block — found live 7 Aug 2026: 8/174 schools have no
    // motto, and the block rendered anyway with a bare dash.
    if (data.founding_year) {
      html += '<div class="scp-profile-block"><div class="k">Founded</div><div style="font-family:var(--rc-serif);font-size:20px;">' + data.founding_year + '</div></div>';
    }
    if (data.motto) {
      html += '<div class="scp-profile-block"><div class="k">School motto</div><div style="font-family:var(--rc-serif);font-style:normal;font-size:16px;">“' + data.motto + '”</div></div>';
    }
    if (data.principals && data.principals.length) {
      html += '<div class="scp-profile-block" style="grid-column:1/-1;"><div class="k">Principals</div>' +
        data.principals.map(function (p) { return '<div class="scp-contact-row"><span>' + (p.term || '') + '</span><b>' + p.name + '</b></div>'; }).join('') +
        '</div>';
    }
    html += '</div>';
    root.innerHTML = html;
  })();

  // ── Affiliation ─────────────────────────────────────────────────────
  // affiliated_secondary items are {secondary_school_name, affiliation_type}.
  // TYPE_B_FULL_SCHOOL means "same school, own secondary section" (Catholic
  // High, CHIJ St Nicholas, Maris Stella) — a straight-through internal
  // progression, not a priority-admission pairing with a different school,
  // and a genuinely stronger selling point for a parent. Rendered with its
  // own copy/badge rather than folded into the normal affiliation row.
  (function renderAffiliation() {
    // The whole <section> only exists in the DOM when Liquid found a
    // non-empty affiliated_secondary array (school-affiliation.liquid) — no
    // show/hide toggle needed here anymore, just populate.
    var root = document.getElementById('scp-affiliation-root');
    var profile = readJSON('scp-profile-data');
    if (!root || !profile) return;
    var list = profile.affiliated_secondary || [];
    if (!list.length) return;
    root.innerHTML = list.map(function (item) {
      var isSameSchool = item.affiliation_type === 'TYPE_B_FULL_SCHOOL';
      var badge = isSameSchool
        ? '<span class="scp-chip" style="margin-left:8px;">Same school</span>'
        : '';
      var lede = isSameSchool
        ? '<div class="scp-affil-row"><span class="scp-affil-arrow">&rarr;</span><span>Straight through to ' + item.secondary_school_name + ' — this is the same school’s own secondary section, not a separate affiliation.' + badge + '</span></div>'
        : '<div class="scp-affil-row"><span class="scp-affil-arrow">&rarr;</span><span>' + item.secondary_school_name + '</span></div>';
      return lede;
    }).join('');
  })();

  // ── Cost strip (ring PSF stats, from nearby projects) ──────────────
  // Split by property_type, not blended — landed PSF is computed on land
  // area and condo/EC PSF on strata area, so a single "ring median" mixing
  // them (the original implementation) reads as one number when it's really
  // two incomparable ones. Found live 7 Aug 2026 on Nanyang Primary: WATTEN
  // ESTATE (Landed, $2,586 psf) sat next to THE TRESOR (Apartment & Condo,
  // $2,558 psf) in one blended median as if equivalent — a ~$13M house next
  // to a ~$2.8M apartment. Executive Condominium is strata-area based like
  // Apartment & Condo (not land-area based like Landed/Strata Landed), so it
  // groups with condo for the headline, not with the landed rows.
  var STRATA_TYPES = ['Apartment & Condo', 'Executive Condominium'];
  var LANDED_TYPES = ['Landed', 'Strata Landed'];

  function _statsFor(list) {
    if (!list.length) return null;
    var meds = list.map(function (r) { return r.median_psf; }).filter(function (v) { return v != null; }).sort(function (a, b) { return a - b; });
    if (!meds.length) return null;
    // Lower-of-two-middle on ties, matching BigQuery's APPROX_QUANTILES(x, 2)
    // [OFFSET(1)] convention (see build_school_nearby.py's compute_stat_tiles
    // — same bug, same fix, confirmed 8 Aug 2026 via Catholic High's condo ring).
    var median = meds[Math.floor((meds.length - 1) / 2)];
    var lo = Math.min.apply(null, list.map(function (r) { return r.min_psf != null ? r.min_psf : r.median_psf; }));
    var hi = Math.max.apply(null, list.map(function (r) { return r.max_psf != null ? r.max_psf : r.median_psf; }));
    return { median: median, lo: lo, hi: hi, count: list.length };
  }

  function computeCostStats(rows) {
    var byType = {};
    rows.forEach(function (r) {
      if (r.median_psf == null) return;
      (byType[r.property_type] = byType[r.property_type] || []).push(r);
    });
    var strataRows = [].concat.apply([], STRATA_TYPES.map(function (t) { return byType[t] || []; }));
    return {
      strata: _statsFor(strataRows),
      landedByType: LANDED_TYPES.map(function (t) { return { type: t, stats: _statsFor(byType[t] || []) }; }).filter(function (x) { return x.stats; }),
      totalCount: rows.length,
    };
  }

  var NEARBY = readJSON('scp-nearby-data') || [];

  (function renderCost() {
    var root = document.getElementById('scp-cost-root');
    if (!root) return;
    // NEARBY holds both ring_band '1km' and '1-2km' rows (needed by the
    // projects-table toggle below) — this section is explicitly "within
    // 1km" only (heading + caveat text), so it must filter before
    // aggregating. Missing this filter silently blended both bands into
    // one figure (e.g. Nanyang read 157 projects / $2,060 psf instead of
    // 62 / $2,049) — found live 8 Aug 2026, Ronnie caught it.
    var rows1km = NEARBY.filter(function (r) { return r.ring_band === '1km'; });
    if (!rows1km.length) {
      root.innerHTML = '<div class="scp-empty-note">No resale transactions within 1km in the last 24 months.</div>';
      return;
    }
    var stats = computeCostStats(rows1km);
    var html = '';

    if (stats.strata) {
      html += '<div class="scp-cost-row">' +
        '<div class="scp-cost-tile"><div class="scp-cost-k">Condo/EC median PSF</div><div class="scp-cost-v">' + fmtPsf(stats.strata.median) + '</div></div>' +
        '<div class="scp-cost-tile"><div class="scp-cost-k">Lowest PSF seen</div><div class="scp-cost-v">' + fmtPsf(stats.strata.lo) + '</div></div>' +
        '<div class="scp-cost-tile"><div class="scp-cost-k">Highest PSF seen</div><div class="scp-cost-v">' + fmtPsf(stats.strata.hi) + '</div></div>' +
        '</div>';
    } else {
      html += '<div class="scp-empty-note">No condo or EC resale transactions within 1km in the last 24 months.</div>';
    }

    if (stats.landedByType.length) {
      html += '<div class="scp-cost-landed-rows">';
      stats.landedByType.forEach(function (x) {
        html += '<div class="scp-contact-row"><span>' + x.type + ' median PSF (land area)</span><b>' + fmtPsf(x.stats.median) + '</b></div>';
      });
      html += '</div>' +
        '<div class="scp-caveat">Landed PSF is computed on land area, not strata/floor area — it is not directly comparable to the condo/EC figure above.</div>';
    }

    html += '<div class="scp-caveat">Across ' + stats.totalCount + ' project' + (stats.totalCount === 1 ? '' : 's') + ' within 1km, last 24 months of resale transactions. Ring membership is matched against MOE’s own building-boundary data, not a straight-line radius — see the table below for per-project distance, shown for context only.</div>';
    root.innerHTML = html;
  })();

  // ── Projects table (sortable, 1km / 1-2km band toggle) ──────────────
  // MOE's Phase 2C P1 priority is banded — within 1km, then 1-2km, then
  // beyond — so which band a project sits in is a real eligibility signal,
  // not just extra content. 1km is the default view (added 8 Aug 2026).
  (function renderProjects() {
    var root = document.getElementById('scp-projects-root');
    if (!root) return;
    if (!NEARBY.length) {
      root.innerHTML = '<div class="scp-empty-note">No resale transactions within 2km in the last 24 months.</div>';
      return;
    }
    var band = '1km';
    var sortKey = 'distance_m', sortAsc = true;
    // Block-level P1 ballot coverage (roncasa.roncasa.project_block_school_ring,
    // see build_block_school_rings.py) — only multi-block New Launch projects
    // carry coverage_pct/blocks_outside (fetch_block_school_coverage() in
    // build_school_nearby.py); every other row's fmt call below sees
    // undefined and prints the column blank, not "0%" — never guess.
    // method='estimated' rows (calibrated Haversine, see roncasa-scripts'
    // build_block_school_rings.py calibrate_bands()) can carry a
    // blocks_borderline count — a deliberate hedge for blocks that landed
    // too close to the 1000m/2000m line to assert either way, counted and
    // shown SEPARATELY from blocks_outside (confidently outside) so this
    // column never implies "avoid" about a block that's merely unconfirmed.
    function fmtCoverage(_v, row) {
      if (row.coverage_pct == null) return '';
      var unconfirmed = row.blocks_unconfirmed || 0;
      var confirmedTotal = row.blocks_total - unconfirmed;
      // ALL blocks unconfirmed (no footprint match AND no safe hedge band --
      // see build_resale_block_school_rings.py) -- there is nothing to put
      // over a denominator ("0/0" read as "0/01 not confirmed" once the
      // blocks_outside span concatenated with no separator, found live
      // 1 Oct 2026 on anglo-chinese-school-junior's Enchanté row). No
      // fraction at all here, just the quiet unconfirmed statement.
      if (confirmedTotal === 0) {
        return '<span class="scp-coverage scp-coverage--unconfirmed">' + unconfirmed +
          ' block' + (unconfirmed === 1 ? '' : 's') + ' · not confirmed by MOE data</span>';
      }
      var full = row.blocks_in_1km === row.blocks_total;
      var cls = full ? 'scp-coverage--full' : 'scp-coverage--partial';
      var prefix = row.method === 'estimated' || row.method === 'mixed' ? 'est. ' : '';
      // unconfirmed blocks are counted OUT of the main fraction's
      // denominator, same as the product-panel verdict.
      var label = full ? '100%' : row.blocks_in_1km + '/' + confirmedTotal;
      var subParts = [];
      if (row.blocks_borderline) subParts.push(row.blocks_borderline + ' borderline');
      if (row.blocks_outside && row.blocks_outside.length) subParts.push('Blk ' + row.blocks_outside.join(', ') + ' outside');
      if (unconfirmed) subParts.push(unconfirmed + ' not confirmed');
      var blocksLine = subParts.length
        ? '<span class="scp-coverage__blocks">' + subParts.join(' · ') + '</span>' : '';
      return '<span class="scp-coverage ' + cls + '">' + prefix + label + blocksLine + '</span>';
    }
    var cols = [
      { key: 'project_name', label: 'Project', cls: 'name' },
      { key: 'distance_m', label: 'Distance', fmt: fmtDist },
      { key: 'property_type', label: 'Type' },
      { key: 'tenure_type', label: 'Tenure' },
      { key: 'txn_count_24m', label: 'Txns (24mo)' },
      { key: 'median_psf', label: 'Median PSF', fmt: fmtPsf },
      { key: 'last_sale_month', label: 'Last sale', fmt: fmtMonth },
      { key: 'coverage_pct', label: 'Block coverage', fmt: fmtCoverage },
    ];
    var n1km = NEARBY.filter(function (r) { return r.ring_band === '1km'; }).length;
    var n2km = NEARBY.filter(function (r) { return r.ring_band === '1-2km'; }).length;

    function draw() {
      var bandRows = NEARBY.filter(function (r) { return r.ring_band === band; });
      var toggleHtml = '<div class="scp-band-toggle" role="tablist">' +
        '<button type="button" class="scp-band-btn' + (band === '1km' ? ' on' : '') + '" data-band="1km">Within 1km <span class="scp-band-count">' + n1km + '</span></button>' +
        '<button type="button" class="scp-band-btn' + (band === '1-2km' ? ' on' : '') + '" data-band="1-2km">1–2km <span class="scp-band-count">' + n2km + '</span></button>' +
        '</div>' +
        (band === '1-2km' ? '<div class="scp-band-note">MOE Phase 2C second priority tier — considered only after every 1km applicant has been placed.</div>' : '');

      if (!bandRows.length) {
        root.innerHTML = toggleHtml + '<div class="scp-empty-note">No resale transactions in this band in the last 24 months.</div>';
        root.querySelectorAll('[data-band]').forEach(function (btn) {
          btn.addEventListener('click', function () { band = btn.dataset.band; draw(); });
        });
        return;
      }

      var rows = bandRows.slice().sort(function (a, b) {
        var av = a[sortKey], bv = b[sortKey];
        if (av == null) return 1; if (bv == null) return -1;
        if (typeof av === 'string') { av = av.toLowerCase(); bv = bv.toLowerCase(); }
        var cmp = av < bv ? -1 : av > bv ? 1 : 0;
        return sortAsc ? cmp : -cmp;
      });
      var thead = '<thead><tr>' + cols.map(function (c) {
        var on = c.key === sortKey;
        return '<th data-key="' + c.key + '" class="' + (on ? 'on ' + (sortAsc ? 'asc' : 'desc') : '') + '">' + c.label + '</th>';
      }).join('') + '</tr></thead>';
      var tbody = '<tbody>' + rows.map(function (r) {
        return '<tr>' + cols.map(function (c) {
          var v = r[c.key];
          var out = c.fmt ? c.fmt(v, r) : (v == null ? '—' : v);
          return '<td class="' + (c.cls || '') + '">' + out + '</td>';
        }).join('') + '</tr>';
      }).join('') + '</tbody>';
      root.innerHTML = toggleHtml + '<div class="scp-table-wrap"><table class="scp-table">' + thead + tbody + '</table></div>' +
        '<div class="scp-caveat">This list is matched against MOE’s own building-boundary data, not a straight-line radius. The Distance column itself is still straight-line, project point to the school’s point coordinate — shown for context, not the inclusion test, so it can read over 1km even for a project correctly inside the ring. MOE’s official result comes from <a href="https://www.onemap.gov.sg/school" target="_blank" rel="noopener">OneMap School Query &rarr;</a> and can change each year (data refreshes every June).</div>';
      root.querySelectorAll('[data-band]').forEach(function (btn) {
        btn.addEventListener('click', function () { band = btn.dataset.band; draw(); });
      });
      root.querySelectorAll('th').forEach(function (th) {
        th.addEventListener('click', function () {
          var key = th.dataset.key;
          if (key === sortKey) sortAsc = !sortAsc; else { sortKey = key; sortAsc = true; }
          draw();
        });
      });
    }
    draw();
  })();

  // ── New launches near this school (Phase 3B, 1 Oct 2026) ────────────
  // Sourced from sch.new_launches_json (project_block_school_ring directly,
  // NOT school_nearby_projects) -- so a still-BUC multi-block launch with
  // zero resale transactions shows up here, unlike the table above. Already
  // sorted by blocks-within-1km share desc server-side; no client sort.
  (function renderNewLaunches() {
    var root = document.getElementById('scp-newlaunch-root');
    if (!root) return;
    var data = readJSON('scp-newlaunch-data') || [];
    if (!data.length) {
      root.innerHTML = '<div class="scp-empty-note">No multi-block new-launch project within range yet.</div>';
      return;
    }
    function fmtTop(y) { return y ? String(y) : '—'; }
    function fmtNlCoverage(row) {
      var unconfirmed = row.blocks_unconfirmed || 0;
      var confirmedTotal = row.blocks_total - unconfirmed;
      if (confirmedTotal === 0) {
        // Shouldn't reach the table at all post-fix (fetch_new_launches_near_school()
        // now requires >=1 positively-qualifying block), but guard anyway --
        // same "no fraction over a 0 denominator" fix as fmtCoverage().
        return '<span class="scp-coverage scp-coverage--unconfirmed">' + unconfirmed +
          ' block' + (unconfirmed === 1 ? '' : 's') + ' · not confirmed by MOE data</span>';
      }
      var full = row.blocks_in_1km === confirmedTotal;
      var cls = full ? 'scp-coverage--full' : 'scp-coverage--partial';
      var label = row.blocks_in_1km + '/' + confirmedTotal + (full ? ' within 1km' : '');
      var sub = [];
      if (row.blocks_borderline) sub.push(row.blocks_borderline + ' borderline');
      if (row.blocks_outside && row.blocks_outside.length) sub.push('Blk ' + row.blocks_outside.join(', ') + ' outside');
      if (unconfirmed) sub.push(unconfirmed + ' not confirmed');
      var subHtml = sub.length ? '<span class="scp-coverage__blocks">' + sub.join(' · ') + '</span>' : '';
      return '<span class="scp-coverage ' + cls + '">' + label + subHtml + '</span>';
    }
    var rows = '<tbody>' + data.map(function (p) {
      var methodBadge = '<span class="scp-nl-method scp-nl-method--' + p.method + '">' +
        (p.method === 'verified' ? 'Verified' : 'Est.') + '</span>';
      return '<tr>' +
        '<td class="name"><a href="/products/' + p.product_handle + '">' + p.project_name + '</a></td>' +
        '<td>' + fmtTop(p.top_year) + '</td>' +
        '<td>' + fmtNlCoverage(p) + '</td>' +
        '<td>' + methodBadge + '</td>' +
        '</tr>';
    }).join('') + '</tbody>';
    root.innerHTML = '<div class="scp-table-wrap"><table class="scp-table scp-nl-table">' +
      '<thead><tr><th>Project</th><th>TOP</th><th>Block coverage</th><th>Source</th></tr></thead>' +
      rows + '</table></div>' +
      '<div class="scp-caveat">"Verified" is matched against MOE’s own building-boundary data. "Est." is a calibrated straight-line estimate used only where no building exists yet to match against (pre-TOP) — a block flagged borderline is a genuine hedge, not a confirmed verdict; confirm with MOE before relying on one.</div>';
  })();

  // ── Overlap ─────────────────────────────────────────────────────────
  (function renderOverlap() {
    // The whole <section> (and its #scp-overlap-data script tag) only
    // exists in the DOM when Liquid found a non-empty overlap array
    // (school-overlap.liquid) — no show/hide toggle needed here anymore.
    var root = document.getElementById('scp-overlap-root');
    var data = readJSON('scp-overlap-data') || [];
    if (!root || !data.length) return;
    root.innerHTML = data.map(function (o) {
      return '<div class="scp-overlap-row"><a href="/pages/' + o.other_school_slug + '">' + o.other_school_name + '</a>' +
        '<span class="scp-overlap-count">' + o.shared_project_count + ' shared project' + (o.shared_project_count === 1 ? '' : 's') + '</span></div>';
    }).join('');
  })();

  // ── Buses ───────────────────────────────────────────────────────────
  (function renderBuses() {
    var root = document.getElementById('scp-buses-root');
    var data = readJSON('scp-buses-data') || [];
    if (!root) return;
    if (!data.length) {
      root.innerHTML = '<div class="scp-empty-note">No bus stops found within 500m.</div>';
      return;
    }
    root.innerHTML = data.slice().sort(function (a, b) { return a.distance_m - b.distance_m; }).map(function (b) {
      var services = (b.services || []).map(function (s) { return '<span class="scp-bus-svc">' + s + '</span>'; }).join('');
      return '<div class="scp-bus-row">' +
        '<div><div class="scp-bus-name">' + b.stop_name + '</div><div class="scp-bus-road">' + (b.road || '') + '</div></div>' +
        '<div class="scp-bus-services">' + services + '</div>' +
        '<div class="scp-bus-dist">' + fmtDist(b.distance_m) + '</div>' +
        '</div>';
    }).join('');
  })();

  // ── Map — locked-ish pan (~2km view), school pin only (no shading),
  // dotted 1km + 2km ring, condo pins from nearby_json. Mirrors
  // schools-full-listing.liquid's Leaflet setup + version pins. ──────────
  (function renderMap() {
    var mapEl = document.getElementById('scp-leaflet-map');
    var profile = readJSON('scp-profile-data');
    if (!mapEl || typeof L === 'undefined' || !profile || profile.lat == null || profile.lng == null) return;

    var center = [profile.lat, profile.lng];
    var VIEW_RADIUS_M = 2200; // "roughly a 2km view around the school"
    var latDelta = VIEW_RADIUS_M / 111320;
    var lngDelta = VIEW_RADIUS_M / (111320 * Math.cos(center[0] * Math.PI / 180));
    var bounds = [
      [center[0] - latDelta, center[1] - lngDelta],
      [center[0] + latDelta, center[1] + lngDelta],
    ];

    var map = L.map(mapEl, {
      center: center, zoom: 15, minZoom: 13, maxZoom: 18,
      scrollWheelZoom: false,
      maxBounds: bounds, maxBoundsViscosity: 0.8, // constrained pan — not a hard island-wide free-roam
    });
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', { attribution: '&copy; OpenStreetMap', maxZoom: 19 }).addTo(map);

    var mapContainerEl = map.getContainer();
    var zoomHintEl = document.createElement('div');
    zoomHintEl.className = 'scp-map-zoom-hint';
    zoomHintEl.style.cssText = 'position:absolute;bottom:8px;right:8px;z-index:400;font-family:var(--rc-mono);font-size:var(--rc-mono-min, 12px);background:rgba(14,14,14,.7);color:#fff;padding:4px 8px;pointer-events:none;transition:opacity .3s;';
    zoomHintEl.textContent = 'Ctrl + scroll to zoom';
    mapContainerEl.appendChild(zoomHintEl);
    mapContainerEl.addEventListener('wheel', function (e) {
      if (e.ctrlKey || e.metaKey) {
        e.preventDefault();
        if (e.deltaY < 0) map.zoomIn(); else map.zoomOut();
        zoomHintEl.style.opacity = '0';
      }
    }, { passive: false });
    mapContainerEl.addEventListener('mouseleave', function () { zoomHintEl.style.opacity = ''; });

    // School — pin only, no shaded land area (per spec).
    var schoolIcon = L.divIcon({
      className: '', iconSize: [22, 22], iconAnchor: [11, 11],
      html: '<div style="width:22px;height:22px;border-radius:999px;background:var(--rc-ink);border:2px solid #fff;box-shadow:0 1px 4px rgba(0,0,0,.4);"></div>',
    });
    L.marker(center, { icon: schoolIcon }).addTo(map).bindPopup('<b>' + (document.title || 'School') + '</b>');

    // Dotted rings — 1km solid-weight, 2km lighter, both dashed (never filled solid).
    L.circle(center, { radius: 1000, color: 'var(--rc-ink)'.trim() === 'var(--rc-ink)' ? '#0E0E0E' : '#0E0E0E', weight: 1.5, fill: false, dashArray: '2,6' }).addTo(map);
    L.circle(center, { radius: 2000, color: '#8A8A8A', weight: 1, fill: false, dashArray: '2,6' }).addTo(map);

    // Condo pins from nearby_json (enriched with lat/lng by build_school_nearby.py).
    NEARBY.forEach(function (r) {
      if (r.lat == null || r.lng == null) return;
      var icon = L.divIcon({
        className: '', iconSize: [14, 14], iconAnchor: [7, 7],
        html: '<div style="width:14px;height:14px;border-radius:999px;background:var(--rc-ink-2, #525252);border:1.5px solid #fff;box-shadow:0 1px 3px rgba(0,0,0,.3);"></div>',
      });
      L.marker([r.lat, r.lng], { icon: icon }).addTo(map)
        .bindPopup('<b>' + r.project_name + '</b><br>' + fmtDist(r.distance_m) + ' &middot; ' + fmtPsf(r.median_psf) + ' psf median');
    });

    // Bus stop pins — second, toggleable layer, visually distinct (small
    // square vs the round condo/school pins) from sch.buses_json (also
    // enriched with lat/lng by build_school_nearby.py, joined from
    // roncasa.roncasa.bus_stops). Off by default — the map's primary job is
    // the school/condo picture; bus stops are an on-demand overlay.
    var BUSES = readJSON('scp-buses-data') || [];
    var busLayer = L.layerGroup();
    BUSES.forEach(function (b) {
      if (b.lat == null || b.lng == null) return;
      var icon = L.divIcon({
        className: '', iconSize: [12, 12], iconAnchor: [6, 6],
        html: '<div style="width:12px;height:12px;background:var(--rc-wa, #25D366);border:1.5px solid #fff;box-shadow:0 1px 3px rgba(0,0,0,.3);"></div>',
      });
      L.marker([b.lat, b.lng], { icon: icon }).addTo(busLayer)
        .bindPopup('<b>' + b.stop_name + '</b><br>' + (b.road || '') + '<br>' + (b.services || []).join(', '));
    });
    var busToggle = document.getElementById('scp-map-bus-toggle');
    if (busToggle) {
      busToggle.addEventListener('change', function () {
        if (busToggle.checked) map.addLayer(busLayer); else map.removeLayer(busLayer);
      });
    }
  })();

  // ── Ring checker — free, no gating. Geocodes via OneMap's public search
  // API directly from the browser (no ONEMAP_TOKEN required for basic
  // address/postal search — confirmed live 7 Aug 2026: the endpoint below
  // returns results with no auth header). This is a deliberate, documented
  // exception to "only nl-location-widget.liquid calls an external API from
  // the browser" — that rule exists to keep secrets server-side; this call
  // carries no secret and returns only public geocoding data.
  //
  // Verdict source (added 8 Aug 2026 — see KB "Ring checker: precompute,
  // don't query live"): the geocoded postal code is looked up against
  // GET https://api.roncasa.co/school-ring?postal=XXXXXX, which serves a
  // KV-cached map precomputed via ST_CONTAINS against MOE's own
  // school_building_proximity building-boundary polygons (roncasa-scripts/
  // scripts/upload_to_kv.py --key sg_school_rings).
  //   HIT  — this postal code is in MOE's building index. Verdict is a real
  //          in/out against school_1km/school_2km membership, labelled
  //          "Matched against MOE's building-boundary data."
  //   MISS — postal code not indexed (new address, or a landed house outside
  //          a named estate/cluster). Falls back to client-side Haversine
  //          straight-line distance against the school's own point
  //          coordinate, labelled distinctly as an estimate. The MOE-match
  //          label must NEVER appear on a Haversine result.
  // ───────────────────────────────────────────────────────────────────
  window.RoncasaRingChecker = window.RoncasaRingChecker || {};
  (function initRingChecker() {
    var forms = document.querySelectorAll('[data-scp-ring-checker]');
    if (!forms.length) return;

    var SCHOOL_RING_API = 'https://api.roncasa.co/school-ring';

    function haversineM(lat1, lng1, lat2, lng2) {
      var R = 6371000, toRad = function (d) { return d * Math.PI / 180; };
      var dLat = toRad(lat2 - lat1), dLng = toRad(lng2 - lng1);
      var a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) * Math.sin(dLng / 2);
      return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    }

    forms.forEach(function (form) {
      var input = form.querySelector('.scp-ring-input');
      var btn = form.querySelector('.scp-ring-btn');
      var resultEl = form.parentElement.querySelector('.scp-ring-result');
      var errorEl = form.parentElement.querySelector('.scp-ring-error');
      var targetLat = parseFloat(form.dataset.targetLat);
      var targetLng = parseFloat(form.dataset.targetLng);
      var targetName = form.dataset.targetName || 'this school';

      // Allows the multi-school listing pages (schools-district-N /
      // schools-{town}) to point this same snippet at whichever school the
      // visitor has selected, without duplicating the checker markup.
      window.RoncasaRingChecker.setTarget = function (lat, lng, name) {
        targetLat = lat; targetLng = lng; targetName = name || targetName;
        form.dataset.targetLat = lat; form.dataset.targetLng = lng; form.dataset.targetName = targetName;
      };

      // Three distinct outcomes, never worded alike: within 1km (MOE-verified),
      // 1-2km (MOE-verified, Phase 2C), and not-in-index (Haversine estimate).
      function renderMoeMatch(address, schools1km, schools2km) {
        var verdictCls, verdictText, detailText;
        if (schools1km.indexOf(targetName) !== -1) {
          verdictCls = 'pos'; verdictText = 'MOE-verified: inside the 1km priority band';
          detailText = address + ' falls inside ' + targetName + '’s 1km priority band, matched against MOE’s own building-boundary data.';
        } else if (schools2km.indexOf(targetName) !== -1) {
          verdictCls = 'caution'; verdictText = 'MOE-verified: inside the 1–2km band (Phase 2C)';
          detailText = address + ' falls inside ' + targetName + '’s 1–2km band — MOE’s Phase 2C second priority tier, considered only after every 1km applicant is placed. Matched against MOE’s own building-boundary data.';
        } else {
          verdictCls = 'neg'; verdictText = 'MOE-verified: outside both priority bands';
          detailText = address + ' falls outside ' + targetName + '’s 1km and 1–2km bands, matched against MOE’s own building-boundary data.';
        }
        resultEl.innerHTML =
          '<div class="scp-ring-verdict ' + verdictCls + '">' + verdictText + '</div>' +
          '<div class="scp-ring-detail">' + detailText + '</div>' +
          '<div class="scp-ring-source scp-ring-source--moe">Matched against MOE’s building-boundary data.</div>' + onemapCheckHtml();
        resultEl.classList.add('show');
      }

      function renderEstimate(address, lat, lng) {
        var distM = haversineM(lat, lng, targetLat, targetLng);
        var verdictCls, verdictText;
        if (distM <= 1000) { verdictCls = 'pos'; verdictText = 'Estimate: likely inside the 1km band'; }
        else if (distM <= 2000) { verdictCls = 'caution'; verdictText = 'Estimate: likely inside the 1–2km band'; }
        else { verdictCls = 'neg'; verdictText = 'Estimate: likely outside both priority bands'; }
        var gapNote = '';
        if (distM > 1000 && distM <= 1150) gapNote = ' It is about ' + Math.round(distM - 1000) + ' m past the 1 km line by our measure; MOE measures to the school boundary, so it may still count as within 1 km.';
        else if (distM > 2000 && distM <= 2150) gapNote = ' It is about ' + Math.round(distM - 2000) + ' m past the 2 km line by our measure; MOE measures to the school boundary, so it may still count as within 2 km.';
        if (distM > 900 && distM < 1100) verdictText += ' — close to the 1km line, borderline';

        resultEl.innerHTML =
          '<div class="scp-ring-verdict ' + verdictCls + '">' + verdictText + '</div>' +
          '<div class="scp-ring-detail">' + address + ' is approximately <b>' + fmtDist(distM) +
          '</b> in a straight line from ' + targetName + ' — not an MOE-verified match.' + gapNote + '</div>' +
          '<div class="scp-ring-source scp-ring-source--estimate">Estimated — straight-line distance. ' +
          'This address isn’t in MOE’s building index, so treat it as indicative only.</div>' + onemapCheckHtml();
        resultEl.classList.add('show');
      }

      form.addEventListener('submit', function (e) {
        e.preventDefault();
        var q = (input.value || '').trim();
        errorEl.classList.remove('show');
        resultEl.classList.remove('show');
        if (!q) return;
        if (isNaN(targetLat) || isNaN(targetLng)) {
          errorEl.textContent = 'This school has no location on file yet.';
          errorEl.classList.add('show');
          return;
        }
        btn.disabled = true;
        btn.textContent = 'Checking…';

        var url = 'https://www.onemap.gov.sg/api/common/elastic/search?searchVal=' +
          encodeURIComponent(q) + '&returnGeom=Y&getAddrDetails=Y&pageNum=1';
        fetch(url).then(function (res) {
          if (!res.ok) throw new Error('OneMap lookup returned ' + res.status);
          return res.json();
        }).then(function (body) {
          var hit = body.results && body.results[0];
          if (!hit) {
            btn.disabled = false;
            btn.textContent = 'Check distance';
            errorEl.textContent = 'Couldn’t find that address — try a postal code or block + street name.';
            errorEl.classList.add('show');
            return;
          }
          var lat = parseFloat(hit.LATITUDE), lng = parseFloat(hit.LONGITUDE);
          var postal = (hit.POSTAL || '').trim();

          function finishEstimate() { renderEstimate(hit.ADDRESS, lat, lng); btn.disabled = false; btn.textContent = 'Check distance'; }

          if (!/^\d{6}$/.test(postal)) { finishEstimate(); return; }

          fetch(SCHOOL_RING_API + '?postal=' + encodeURIComponent(postal))
            .then(function (res) { return res.json(); })
            .then(function (ring) {
              btn.disabled = false;
              btn.textContent = 'Check distance';
              if (ring && ring.hit) {
                renderMoeMatch(hit.ADDRESS, ring.schools_1km || [], ring.schools_2km || []);
              } else {
                renderEstimate(hit.ADDRESS, lat, lng);
              }
            })
            .catch(function () { finishEstimate(); });
        }).catch(function (err) {
          btn.disabled = false;
          btn.textContent = 'Check distance';
          errorEl.textContent = 'Lookup failed — please try again (' + err.message + ').';
          errorEl.classList.add('show');
        });
      });
    });
  })();
})();
